import * as React from "react";
import LandingCard from "./landing_card";
import { Avatar, Button, ButtonBase, Skeleton, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchUserActivity } from "../../../scripts/api_v2/landing_page_client";
import useBackend, { useLogin } from "../../../hooks/use_backend";
import { ElindusRedColors, shortenUsername, timeAgo } from "../../../scripts/utils";
import { useAmplitudeTrack, useOpenSite, SITE_SELECTION_SOURCE } from "../../../scripts/amplitude_utils";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";

function UserActivityCard({ ...props }) {
  const login = useLogin();
  const backend = useBackend();

  const [count, setCount] = React.useState(5);
  const { trackButtonClicked } = useAmplitudeTrack();

  let { data: userActivity, isFetching: fetchingUserActivityData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_USER_ACTIVITY, backend.fetch, login.company_name, count],
    queryFn: () => fetchUserActivity(backend.fetch, count),
    refetchInterval: 60000,
    placeholderData: (previousData, previousQuery) => previousData,
  });

  const list = userActivity?.list;
  const total = userActivity?.total;

  React.useEffect(() => {
    total && setCount(Math.min(total, count)); // count cannot be larger than data
  }, [total]);

  return (
    <LandingCard
      title="User Activity"
      tooltip={TOOLTIPS_EN.USER_ACTIVITY}
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...props.sx,
      }}
    >
      <Stack spacing={1}>
        {[...Array(count)].map((_, index) =>
          list && index < list.length ? (
            <ChangeItem
              key={list[index].site_name + list[index].description + list[index].datetime}
              userName={list[index].modified_by_user}
              siteName={list[index].site_name}
              siteId={list[index].site_id}
              description={list[index].description}
              timestamp={list[index].datetime}
              isLoading={false}
            />
          ) : (
            <ChangeItem key={index} isLoading={true} isHidden={!fetchingUserActivityData} />
          ),
        )}
      </Stack>

      {list?.length > 0 ? (
        <Button sx={{ marginTop: 1 }} onClick={() => {
          trackButtonClicked('user_activity_view_more', 'landing_page', { shown_count: count + 5 });
          setCount(count + 5);
        }}>
          View More
        </Button>
      ) : (
        <Button sx={{ marginTop: 1 }} disabled={true}>
          None found
        </Button>
      )}
    </LandingCard>
  );
}

export default UserActivityCard;

const AVATAR_SIZE = 40;

function ChangeItem({ userName, siteId, siteName, timestamp, description, isLoading, isHidden = false }) {
  const openSite = useOpenSite();

  return !isHidden ? (
    <>
      <ButtonBase
        onClick={() => openSite(siteId, SITE_SELECTION_SOURCE.USER_ACTIVITY_CARD)}
        sx={{
          width: "100%",
          alignItems: "center",
          display: "flex",
          textAlign: "left",
          borderRadius: "10px",
          padding: 1,
          "&:hover": { backgroundColor: "#f3f3f3" },
        }}
        disabled={isLoading}
      >
        {isLoading ? (
          <Skeleton variant="circular" width={AVATAR_SIZE} height={AVATAR_SIZE} />
        ) : (
          <Avatar sx={{ bgcolor: ElindusRedColors(userName) }}>{shortenUsername(userName)}</Avatar>
        )}

        <Stack sx={{ flexGrow: 1, marginLeft: 2 }}>
          <Typography variant="body2">{isLoading ? <Skeleton /> : siteName}</Typography>
          <Typography variant="caption">{isLoading ? <Skeleton /> : description}</Typography>
          <Typography variant="caption" color={"#aaa"} sx={{ textAlign: "left" }}>
            {isLoading ? <Skeleton width="100px" /> : timeAgo(new Date(1000 * timestamp))}
          </Typography>
        </Stack>
      </ButtonBase>
    </>
  ) : null;
}
